import { Icon, MenuItem, DialogActions as MuiDialogActions, DialogContent as MuiDialogContent, DialogTitle as MuiDialogTitle } from "@material-ui/core";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Button } from "js/Components/Button";
import { Dropdown } from "js/Components/Dropdown";
import AppController from "js/core/AppController";
import { WorkspaceUserRole } from "common/interfaces";
import { themeColors } from "js/react/sharedStyles";
import { defaultText } from "js/textStyles";
import { FlexBox } from "js/react/components/LayoutGrid";

export const ContentFrame = styled.div<{ dividers?: boolean }>`
    width: 100%;
    padding: 15px;
    background: #F7FAFC;
    border: 1px solid #DAE9F0;
    border-radius: 2px;
    display: flex;
    flex-direction: flex-start;
    align-items: center;
    gap: 15px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    ${({ dividers }) => dividers && `
        >div {
            border-bottom: 1px solid #EEE;
            padding-bottom: 15px;
        }
        >div:last-child {
            border-bottom: none;
            padding-bottom: 0;
        }
    `}
`;

export const DialogBody = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 30px;
    padding: 40px 50px;
    font-family: "Source Sans Pro";
    position: relative;
`;

export const DialogTitle = styled(MuiDialogTitle)`
    &.MuiDialogTitle-root {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 5px;
        padding:0;
    }

    h2 {
        color: #333;
        font-family: "Source Sans Pro";
        text-align: center;
        font-size: 20px;
        font-weight: 600;
        line-height: 120%;

        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 5px;

        p {
            color: #666;
            font-size: 14px;
            font-weight: 400;
            line-height: 125%; 
        }
    }
`;

export const PaddedDialogContent = styled(MuiDialogContent)`
    padding: 0 34px 20px !important;
`;

export const PaddedDialogActions = styled(MuiDialogActions)`
    padding: 0 34px 20px !important;
`;

export const DialogContent = styled(MuiDialogContent)`
    &&& {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 30px;
        padding: 0px;
        width: 100%;
        ${defaultText}
    }
`;

export const DialogActions = styled(MuiDialogActions)`
    &.MuiDialogActions-root {
        padding: 0;
        justify-content: flex-end;
        margin: 0px;
        gap: 20px;
        width: 100%;
        flex-wrap: wrap;
    }
`;

export const ContentFrameTitle = styled.div`
    font-size: 12px;
    font-weight: 600;
    color: #666;
    letter-spacing: 0.5px;
    text-transform: uppercase;
`;

export const HighlightedIcon = styled(Icon).attrs({ fontSize: "small" }) <{ blue?: boolean }>`
    color: ${props => props.blue ? themeColors.ui_blue : "#ffaa00"};
    align-self: flex-start;
`;

export const FeatureText = styled.div<{ dimmed?: boolean }>`
    color: ${props => props.dimmed ? "#666" : "#333"};
    font-family: "Source Sans Pro";
    font-size: 14px;
    font-weight: 400;
    line-height: 110%;
`;

const WorkspaceSelectContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 15px;
    width: 100%;
`;

const WorkspaceSelectDropdownContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    gap: 20px;
    width: 100%;

    > .MuiInputBase-root {
        flex-grow: 1;
    }

    > .bai-button {
        white-space: nowrap;
    }
`;

const DividerContainer = styled.div`
    width: 100%;
    height: 20px;
    position: relative;

    > div {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        width: 100%;
        height: 1px;
        background: #EEE;
    }

    > span {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        color: #666;
        font-size: 12px;
        background: white;
        padding: 10px;
    }
`;

type WorkspaceSelectProps = {
    onSwitchWorkspace: () => void;
    onCreateWorkspace: () => void;
};

export function WorkspaceSelect(props: WorkspaceSelectProps) {
    const { onSwitchWorkspace, onCreateWorkspace } = props;
    const { t } = useTranslation();

    const workspaceId = AppController.workspaceId;

    const otherWorkspaces = [
        ...AppController.workspacesMetadata.filter(metadata => metadata.id !== workspaceId && metadata.userRole === WorkspaceUserRole.OWNER),
        ...AppController.workspacesMetadata.filter(metadata => metadata.id !== workspaceId && metadata.userRole !== WorkspaceUserRole.OWNER)
    ];

    const [selectedWorkspaceId, setSelectedWorkspaceId] = useState(otherWorkspaces[0]?.id);

    const handleGoToWorkspace = () => {
        AppController.switchWorkspace(selectedWorkspaceId);
        onSwitchWorkspace();
    };

    const handleCreateWorkspace = async () => {
        const success = await AppController.createWorkspace();
        if (success) {
            onCreateWorkspace();
        }
    };

    return (
        <WorkspaceSelectContainer>
            {otherWorkspaces.length > 0 && <>
                <WorkspaceSelectDropdownContainer>
                    <Dropdown large value={selectedWorkspaceId} onChange={setSelectedWorkspaceId}>
                        {otherWorkspaces.map(workspace => (
                            <MenuItem key={workspace.id} value={workspace.id}>
                                {workspace.name}
                            </MenuItem>
                        ))}
                    </Dropdown>
                    <Button blue large onClick={handleGoToWorkspace}>
                        {t("Go To Workspace")}
                    </Button>
                </WorkspaceSelectDropdownContainer>
                <DividerContainer>
                    <div />
                    <span>OR</span>
                </DividerContainer>
            </>}
            <Button upgrade large onClick={handleCreateWorkspace}>{t("Create A Workspace")}</Button>
        </WorkspaceSelectContainer>
    );
}

const NeedHelpContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 5px;
    align-items: center;
    justify-content: flex-start;

    >span {
        color: #666;
        font-size: 14px;
        font-weight: 400;
    }

    >a {
        color: #666;
        font-size: 14px;
        font-style: normal;
        font-weight: 600;
        line-height: 150%;
        text-decoration-line: underline;
        text-decoration-style: solid;
        text-decoration-skip-ink: none;
        text-decoration-thickness: auto;
        text-underline-offset: auto;
        text-underline-position: from-font;
    }
`;

export function NeedHelp() {
    const { t } = useTranslation();

    return (
        <NeedHelpContainer>
            <span>{t("Need help?")}</span>
            <a href="mailto:support@beautifulai.com">{t("Contact support")}</a>
        </NeedHelpContainer>
    );
}

export function UpgradeToRestoreSection() {
    const { t } = useTranslation();

    return (
        <ContentFrame>
            <ContentFrameTitle>{t("Upgrade to restore")}</ContentFrameTitle>
            <FlexBox gap={8}>
                <HighlightedIcon>check_circle</HighlightedIcon>
                <FeatureText>{t("No watermarks")}</FeatureText>
            </FlexBox>
            <FlexBox gap={8}>
                <HighlightedIcon>check_circle</HighlightedIcon>
                <FeatureText>{t("Create with AI")}</FeatureText>
            </FlexBox>
            <FlexBox gap={8}>
                <HighlightedIcon>check_circle</HighlightedIcon>
                <FeatureText>{t("Track analytics")}</FeatureText>
            </FlexBox>
            <FlexBox gap={8}>
                <HighlightedIcon>check_circle</HighlightedIcon>
                <FeatureText>{t("Editable PowerPoint export")}</FeatureText>
            </FlexBox>
        </ContentFrame>
    );
}
